import { useState } from "react";
import type { FormEvent } from "react";

import { useDialogForm } from "@/ds";
import { formatPapelObraJanela } from "@/modules/recursos";

import {
  JUSTIFICATIVA_MAX_LENGTH,
  JUSTIFICATIVA_REQUIRED_MESSAGE,
} from "./inativar-papel-obra-dialog.constants";
import type {
  InativarPapelObraDialogProps,
  InativarPapelObraDialogViewModel,
} from "./types-inativar-papel-obra-dialog";

/** Liga o botão de confirmação, que vive no rodapé do Dialog, ao formulário do corpo. */
const FORM_ID = "inativar-papel-obra-form";

type Estado = { justificativa: string };

// Só espaços conta como vazio: o front não envia justificativa em branco.
const validar = ({ justificativa }: Estado) =>
  justificativa.trim() === "" ? { justificativa: JUSTIFICATIVA_REQUIRED_MESSAGE } : {};

/**
 * Modal "Inativar Papel" (HU 2037): identificação do papel somente para consulta e justificativa obrigatória.
 * Recusas do serviço mantêm o modal aberto para nova tentativa.
 */
export function useInativarPapelObraDialog({
  papel,
  onConfirm,
  onCancel,
}: InativarPapelObraDialogProps): InativarPapelObraDialogViewModel {
  const [tocada, setTocada] = useState(false);
  const [fieldServerError, setFieldServerError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useDialogForm<Estado>({
    initial: { justificativa: "" },
    validate: validar,
    onSubmit: async ({ justificativa }) => {
      const failure = await onConfirm(justificativa.trim());
      if (!failure) return;

      if (failure.kind === "invalid") setFieldServerError(failure.message);
      else setServerError(failure.message);
    },
  });

  const { justificativa } = form.values;

  return {
    nome: papel.nome,
    janelaTexto: formatPapelObraJanela(papel),
    justificativa: {
      value: justificativa,
      error:
        fieldServerError ??
        form.errors.justificativa ??
        (tocada ? (validar(form.values).justificativa ?? null) : null),
      digitar: (texto) => {
        setFieldServerError(null);
        form.setValue("justificativa", texto.slice(0, JUSTIFICATIVA_MAX_LENGTH));
      },
      sairDo: () => setTocada(true),
    },
    serverError,
    isSubmitting: form.isSubmitting,
    formId: FORM_ID,
    confirmar: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setTocada(true);
      setFieldServerError(null);
      setServerError(null);
      void form.submit();
    },
    cancelar: onCancel,
  };
}
